import React, { FC, useMemo, useCallback, useEffect } from 'react';
import { TaskItem } from '@demind-inc/core';
import dayjs from 'dayjs';
import { Checkbox, CircularProgress, Tooltip, Typography } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import clsx from 'clsx';
import { useSetRecoilState } from 'recoil';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';

import './TaskCard.scss';
import { TODO_INTEGRATION_ICONS } from '../../../components/TodoIntegration/constants';
import {
  DEFAULT_TASK_DURATION_MIN,
  useTaskActions,
  useTodoTasksContext,
  visibleTaskDetailAtom,
} from '../../../data-access';
import { useGeneralSettings, useTaskSettings, useTaskTimeSuggestion } from '../../../hooks';
import sparklesWhiteIcon from '../../../assets/scheduler/sparkles_white.svg';
import { getDueDateYYYYMMDD } from '../../../helpers';
import { isAppleRemindersDesktopSupported } from '../../../helpers/appleRemindersDesktop.helpers';
import { formatDataDate } from '../../DataView/i18n';

interface TaskCardProps {
  task: TaskItem;
  className?: string;
  draggable?: boolean;
}

const TaskCard: FC<TaskCardProps> = ({ task, className, draggable = false }) => {
  const { t, i18n } = useTranslation();
  const { findProject } = useTodoTasksContext();
  const { generalSettings } = useGeneralSettings();
  const { taskSettings } = useTaskSettings();
  const { updateTodoTaskStatus, updateTodoTask } = useTaskActions();
  const setVisibleTask = useSetRecoilState(visibleTaskDetailAtom);

  const timeFormat = generalSettings?.timeFormat || '12h';
  const defaultDurationMin = taskSettings?.durationMin || DEFAULT_TASK_DURATION_MIN;

  const project = useMemo(() => findProject(task.boardId), [findProject, task.boardId]);

  const dueDateLabel = useMemo(() => {
    const dueDate = getDueDateYYYYMMDD(task.dueDateTime);
    if (!dueDate) {
      return t('task.card.noDueDate');
    }

    const dueDateDayjs = dayjs(dueDate);
    if (dueDateDayjs.isSame(dayjs(), 'day')) {
      return t('task.sections.today');
    }

    if (dueDateDayjs.isSame(dayjs().add(1, 'day'), 'day')) {
      return t('task.sections.tomorrow');
    }

    return formatDataDate(i18n.language, dueDateDayjs.toDate(), {
      month: 'short',
      day: 'numeric',
    });
  }, [i18n.language, task.dueDateTime, t]);

  const durationLabel = useMemo(() => {
    const durationMin = task.duration?.amount || defaultDurationMin;
    if (durationMin < 60) {
      return t('task.duration.minutesShort', { minutes: durationMin });
    }
    const hours = Math.floor(durationMin / 60);
    const minutes = durationMin % 60;
    return minutes
      ? t('task.duration.hoursMinutesShort', { hours, minutes })
      : t('task.duration.hoursShort', { hours });
  }, [defaultDurationMin, task.duration?.amount, t]);

  const hasScheduledTime = !!task.dueDateTime?.datetime;
  const isAiSuggestedTime = !!task.dueDateTime?.autoSuggested;
  const shouldUseAiTimeStyle = !hasScheduledTime || isAiSuggestedTime;

  const taskTimeLabel = useMemo(() => {
    if (!task.dueDateTime?.datetime) {
      return t('task.card.smartSchedule');
    }

    const dueDateTime = dayjs(task.dueDateTime.datetime);
    const formattedTime = dueDateTime.format(timeFormat === '12h' ? 'h:mm A' : 'HH:mm');

    return `${formattedTime}`;
  }, [task.dueDateTime?.datetime, timeFormat, t]);

  const suggestionDate = useMemo(() => {
    return getDueDateYYYYMMDD(task?.dueDateTime) || dayjs().format('YYYY-MM-DD');
  }, [task?.dueDateTime]);

  const { requestSuggestion, isPending: isSuggestingTime } = useTaskTimeSuggestion({
    date: suggestionDate,
    task: {
      ...task,
      duration: task.duration || { amount: defaultDurationMin, unit: 'minute' },
      dueDateTime: {
        ...(task.dueDateTime ?? {}),
        date: suggestionDate,
        timezone: task.dueDateTime?.timezone ?? dayjs.tz.guess(),
      },
    },
    suggestionKey: task.taskId ?? task.originalTaskId,
  });

  const projectName = project?.name || t('task.project.inbox');
  const projectIcon = TODO_INTEGRATION_ICONS[task.appFrom];

  const isAppleRemindersBlocked =
    task.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported();

  const isCompletionDisabled =
    isAppleRemindersBlocked || (task.recurring?.isRecurring === true && task.completed);

  const disabledCheckboxTooltip =
    task.recurring?.isRecurring === true && task.completed
      ? t('task.modal.cannotReopenCompletedOccurrence')
      : isAppleRemindersBlocked
        ? t('task.project.appleRemindersDesktopOnlyWithPeriod')
        : '';

  const handleTaskComplete = useCallback(
    async (completed: boolean) => {
      const dueDate = getDueDateYYYYMMDD(task.dueDateTime);

      try {
        await updateTodoTaskStatus({
          taskId: task.taskId,
          boardId: task.boardId,
          completed,
          originalTaskId: task.originalTaskId,
          ...(dueDate
            ? {
                mutateParams: {
                  date: dueDate,
                },
              }
            : {}),
        });
      } catch (error) {
        console.error('Failed to update task status:', error);
      }
    },
    [updateTodoTaskStatus, task.taskId, task.boardId, task.dueDateTime]
  );

  const handleSuggestTime = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (isAppleRemindersBlocked || task.completed) {
        return;
      }

      await requestSuggestion(async (suggestedTime) => {
        const taskTz = task.dueDateTime?.timezone ?? dayjs.tz.guess();
        await updateTodoTask({
          taskId: task.taskId,
          boardId: task.boardId,
          newTaskInfo: {
            ...task,
            dueDateTime: {
              ...(task.dueDateTime ?? {}),
              timezone: taskTz,
              datetime: suggestedTime.toISOString(),
              date: dayjs(suggestedTime).tz(taskTz).format('YYYY-MM-DD'),
            },
            addedToCalendar: true,
          },
          shouldReflectToEvent: true,
        });
      });
    },
    [requestSuggestion, task, updateTodoTask]
  );

  const handleClick = useCallback(() => {
    setVisibleTask(task);
  }, [setVisibleTask, task]);

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: 'TASK',
      item: task,
      canDrag: () => draggable && !task.completed,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [task, draggable]
  );

  useEffect(() => {
    if (draggable) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [draggable, preview]);

  const checkboxElement = (
    <span
      className={clsx('task-card__checkbox-wrapper', {
        'task-card__checkbox-wrapper--disabled': isCompletionDisabled,
      })}
      onClick={(event) => event.stopPropagation()}
    >
      <Checkbox
        className="task-card__checkbox"
        checked={task.completed}
        onClick={(event) => event.stopPropagation()}
        onChange={(_, checked) => handleTaskComplete(checked)}
        disabled={isCompletionDisabled}
      />
    </span>
  );

  return (
    <div
      className={clsx('task-card', className, {
        'task-card--dragging': isDragging,
        'task-card--draggable': draggable && !task.completed,
      })}
      onClick={handleClick}
    >
      {draggable && !task.completed && (
        <div ref={drag} className="task-card__drag-handle" onClick={(e) => e.stopPropagation()}>
          <DragIndicator className="task-card__drag-handle__icon" />
        </div>
      )}
      <div className="task-card__main">
        {disabledCheckboxTooltip ? (
          <Tooltip title={disabledCheckboxTooltip} arrow>
            {checkboxElement}
          </Tooltip>
        ) : (
          checkboxElement
        )}
        <Typography
          variant="body1"
          className={clsx('task-card__title', {
            'task-card__title--done': task.completed,
          })}
        >
          {task.name}
        </Typography>
      </div>

      <div className="task-card__meta">
        <Typography variant="subtitle2" className="task-card__chip task-card__chip--due-date">
          {dueDateLabel}
        </Typography>

        {hasScheduledTime ? (
          <Typography
            variant="subtitle2"
            className={clsx('task-card__chip task-card__chip--time', {
              'task-card__chip--schedule': shouldUseAiTimeStyle,
            })}
          >
            {shouldUseAiTimeStyle && (
              <img src={sparklesWhiteIcon} alt="" className="task-card__chip__sparkle" />
            )}
            <span className="task-card__chip__label">{taskTimeLabel}</span>
          </Typography>
        ) : (
          <button
            type="button"
            className={clsx('task-card__chip task-card__chip--time task-card__chip--schedule', {
              'task-card__chip--schedule-loading': isSuggestingTime,
            })}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={handleSuggestTime}
            disabled={isAppleRemindersBlocked || task.completed || isSuggestingTime}
            aria-label={t('task.card.smartSchedule')}
          >
            <img src={sparklesWhiteIcon} alt="" className="task-card__chip__sparkle" />
            {isSuggestingTime ? (
              <CircularProgress size={12} className="task-card__chip__loader" />
            ) : (
              <Typography component="span" variant="subtitle2" className="task-card__chip__label">
                {t('task.card.smartSchedule')}
              </Typography>
            )}
          </button>
        )}

        <Typography variant="subtitle2" className="task-card__chip task-card__chip--duration">
          {durationLabel}
        </Typography>

        <div className="task-card__project">
          {projectIcon && (
            <img src={projectIcon} alt={task.appFrom} className="task-card__project__icon" />
          )}
          <Typography variant="body2" className="task-card__project__name">
            {projectName}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
